import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ArtworkImage {
  url: string;
  alt?: string;
}

export interface Artwork {
  id: string;
  slug: string;
  title_nl: string;
  title_en: string;
  description_nl: string | null;
  description_en: string | null;
  materials_nl: string | null;
  materials_en: string | null;
  dimensions: string | null;
  year: number | null;
  price_cents: number | null;
  currency: string;
  sold: boolean;
  published: boolean;
  sort_order: number;
  images: ArtworkImage[];
  created_at?: string;
}

// The generated Supabase types do not include these tables yet.
const db = supabase as unknown as {
  from: (table: string) => any;
};

function normalize(row: any): Artwork {
  return {
    ...row,
    images: Array.isArray(row?.images) ? row.images : [],
  } as Artwork;
}

/** Published artworks for the public "Nieuw werk" page. */
export function useArtworks(includeUnpublished = false) {
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    let query = db
      .from('artworks')
      .select('*')
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: false });
    if (!includeUnpublished) query = query.eq('published', true);

    const { data, error } = await query;
    if (error) setError(error.message);
    else {
      setError(null);
      setArtworks((data ?? []).map(normalize));
    }
    setLoading(false);
  }, [includeUnpublished]);

  useEffect(() => {
    load();
  }, [load]);

  return { artworks, loading, error, reload: load };
}

export function useArtwork(slug?: string) {
  const [artwork, setArtwork] = useState<Artwork | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!slug) return;
    setLoading(true);
    db.from('artworks')
      .select('*')
      .eq('slug', slug)
      .maybeSingle()
      .then(({ data }: { data: any }) => {
        if (cancelled) return;
        setArtwork(data ? normalize(data) : null);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return { artwork, loading };
}

export function formatPrice(artwork: Artwork, lang: 'nl' | 'en'): string | null {
  if (artwork.price_cents == null) return null;
  return new Intl.NumberFormat(lang === 'nl' ? 'nl-NL' : 'en-GB', {
    style: 'currency',
    currency: artwork.currency || 'EUR',
    maximumFractionDigits: 0,
  }).format(artwork.price_cents / 100);
}

export const artworksDb = db;
